import { formatCurrency } from '@/shared';
import * as S from './styles';
import { useTables } from '@/hooks';
import React from 'react';
import { ItemsProps as ItemsInterface } from '@/talons/orderStatusPage/useOrderStatusPage';
import { CategoryProps, PackagesTypesProps } from '@/types';
import { pxToPdf } from '@/shared/commonFunctions';
import { usePackingInfos } from '@/shared/packing';
import { buildImageSrc, buildImageSrcError } from '@/shared';

interface ItemsProps extends ItemsInterface {
  INDICE: number;
  CATEGORIA: CategoryProps;
}

interface Props {
  productsByCategory: Record<number, ItemsProps[]>;
}

const SharedOrderList = ({ productsByCategory }: Props) => {
  const { tables } = useTables();
  const tableImages = tables.current.imagens;
  const categorias = tables.current.dicionario_categorias;
  
  const getImage = (product: ItemsProps) => {
    return buildImageSrc(product?.IMAGEM || tableImages?.[product?.COD_PRODUTO]);
  }

  return (
    <S.ContentTable>
      <S.Table>
        <S.THead>
          <S.TRow>
            <S.THeadCell>Item</S.THeadCell>
            <S.THeadCell>Código</S.THeadCell>
            <S.THeadCell>EAN</S.THeadCell>
            <S.THeadCell>Descrição do produto</S.THeadCell>
            <S.THeadCell>Embalagem</S.THeadCell>
            <S.THeadCell>Qtd.</S.THeadCell>
            <S.THeadCell>Preço unitário</S.THeadCell>
            <S.THeadCell>Preço emb.</S.THeadCell>
            <S.THeadCell>ST</S.THeadCell>
            <S.THeadCell style={{ textAlign: 'right' }}>Preço total</S.THeadCell>
          </S.TRow>
        </S.THead>

        <S.TBody>
          {Object.entries(productsByCategory).map(([cod, products]) => (
            <React.Fragment key={`${cod}-${products?.[0]?.COD_PRODUTO}`}>
              <S.TRow>
                <S.TCell colSpan={10} style={{ paddingBottom: pxToPdf(10), paddingTop: pxToPdf(10) }}>
                  <S.BoldText style={{ fontSize: '12px' }}>
                    {categorias[Number(cod)]?.DE_CATEGORIA || 'Generalista'} ({products.length})
                  </S.BoldText>
                </S.TCell>
              </S.TRow>

              {products.map((product) => {
                const { packaging, EMBALAGEM } = product
                
                const { SG_EMBALAGEM, QTD_EMBALAGEM: QTD, QTD_MULTIPLO_VENDA } = EMBALAGEM?.find((pk) => {
                  const isMultiple = (pk.QTD_MULTIPLO_VENDA || 0) > 1
                  const emb = `${pk.SG_EMBALAGEM}_${isMultiple ? pk.QTD_MULTIPLO_VENDA : pk.QTD_EMBALAGEM}`
                  return emb === packaging
                }) || {};
                
                const embalagem = usePackingInfos(SG_EMBALAGEM as PackagesTypesProps);
                const ean = product.ATRIBUTOS?.find((at) => at.ean_grp)?.ean_grp;
                const sku = product.COD_PRODUTO;
                
                const isMultiple = (QTD_MULTIPLO_VENDA || 0) > 1
                const QTD_EMBALAGEM = isMultiple ? QTD_MULTIPLO_VENDA : QTD;
                
                return (
                  <S.TRow key={`${cod}-${product.COD_PRODUTO}`} $bordered $minHeight style={{ position: 'relative', borderTop: '1px solid #E9E8E7' }}>
                    <S.TCell>
                      {product.INDICE}
                    </S.TCell>

                    <S.TCell>
                      {sku}
                    </S.TCell>

                    <S.TCell>
                      {ean}
                    </S.TCell>

                    <S.TCell style={{ maxWidth: '180px' }}>
                      <S.Row style={{ alignItems: 'center', minHeight: '42px', }}>
                        <img
                          style={{ width: '18px', marginTop: '4px' }}
                          src={getImage(product)}
                          onError={buildImageSrcError}
                        />
                        <S.Text style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          maxWidth: '180px',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: '14px',
                          maxHeight: '42px',
                        }}>
                          {product?.DESC_PRODUTO || product?.product}
                        </S.Text>
                      </S.Row>
                    </S.TCell>

                    <S.TCell>

                      <S.Row>
                        <img src={embalagem?.icon} style={{ width: '12px', marginTop: '4px' }} />
                        <S.Col>
                          <S.BoldText>{embalagem?.packingTitle}</S.BoldText>
                          <S.Text>
                            {QTD_EMBALAGEM} {embalagem?.measureUnit}{!embalagem?.measureUnit ? '' : QTD_EMBALAGEM === 1 ? '' : 's'}
                          </S.Text>
                        </S.Col>
                      </S.Row>
                    </S.TCell>

                    <S.TCell>

                      <S.Box>
                        <S.BoldText>{product.quantity}</S.BoldText>
                      </S.Box>
                    </S.TCell>

                    <S.TCell>
                      <S.Box padding={7}>
                        <S.Text style={{ color: '#828282' }}>
                          {formatCurrency({ value: product.unitPricePerItem })}
                        </S.Text>
                      </S.Box>
                    </S.TCell>

                    <S.TCell>
                      <S.Text style={{ color: '#999', fontWeight: 600 }}>
                        {formatCurrency({ value: product.unitPrice })}
                      </S.Text>
                    </S.TCell>

                    <S.TCell>
                      <S.Text style={{ color: '#999', fontWeight: 600 }}>
                        {formatCurrency({ value: product.stPrice })}
                      </S.Text>
                    </S.TCell>

                    <S.TCell>
                      <S.BoldText style={{ justifyContent: 'flex-end', width: '100%' }}>{formatCurrency({ value: product.price })}</S.BoldText>
                    </S.TCell>
                  </S.TRow>
                );
              })}
            </React.Fragment>
          ))}
        </S.TBody>
      </S.Table>
    </S.ContentTable>
  );
};

export default SharedOrderList;